import React, { useState } from "react";
import { Modal, Button, Radio, Input, Space, Typography, Form, Table, Popconfirm, Divider, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

interface AIReviewPayload {
  correction_type: "freeform" | "choices";
  choices?: string[];
}

interface AIReviewProps {
  isVisible: boolean;
  onClose: () => void;
  onStartReview: (payload: AIReviewPayload) => void;
  loading: boolean;
}

const AIReview: React.FC<AIReviewProps> = ({ isVisible, onClose, onStartReview, loading }) => {
  const [correctionType, setCorrectionType] = useState<"freeform" | "choices">("freeform");
  const [choices, setChoices] = useState<string[]>([""]);
  const [savedChoices, setSavedChoices] = useState<string[]>([""]);
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState<string>("0");

  const isEditing = (record: { key: string }) => record.key === editingKey;

  const handleStartReview = () => {
    const payload: AIReviewPayload = {
      correction_type: correctionType,
      ...(correctionType === "choices" && { choices: choices.filter(c => c.trim()) })
    };
    onStartReview(payload);
  };

  const handleCorrectionTypeChange = (e: any) => {
    const newType = e.target.value;
    setCorrectionType(newType);
    if (newType === "choices") {
      // Restore saved choices when switching back to multiple choice
      setChoices(savedChoices);
      setEditingKey(savedChoices.length === 1 ? "0" : "");
    } else {
      // Save current choices when switching away from multiple choice
      setSavedChoices(choices);
    }
  };

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
    setSavedChoices(newChoices);
  };

  const addChoice = () => {
    const newChoices = [...choices, ""];
    setChoices(newChoices);
    setSavedChoices(newChoices);
    form.setFieldsValue({ [`choice-${choices.length}`]: "" });
    setEditingKey((choices.length).toString());
  };

  const removeChoice = (index: number) => {
    const newChoices = choices.filter((_, i) => i !== index);
    setChoices(newChoices);
    setSavedChoices(newChoices);
    setEditingKey("");
  };

  const columns = [
    {
      title: "Choice",
      dataIndex: "choice",
      key: "choice",
      width: "80%",
      render: (_: any, record: { key: string; choice: string }) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name={`choice-${record.key}`}
            style={{ margin: 0 }}
            rules={[{ required: true, message: "Please input a choice" }]}
            initialValue=""
          >
            <Input
              onChange={(e) => handleChoiceChange(Number(record.key), e.target.value)}
              placeholder="Enter choice"
              autoFocus
            />
          </Form.Item>
        ) : (
          <span>{record.choice || "-"}</span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: "20%",
      render: (_: any, record: { key: string }) => {
        const editable = isEditing(record);
        return editable ? (
          <Space size="middle">
            <Button type="link" onClick={() => setEditingKey("")} size="small">Save</Button>
            <Button type="link" onClick={() => setEditingKey("")} size="small">Cancel</Button>
          </Space>
        ) : (
          <Space size="middle">
            <Button type="link" onClick={() => setEditingKey(record.key)} size="small">Edit</Button>
            <Popconfirm
              title="Are you sure you want to delete this choice?"
              onConfirm={() => removeChoice(Number(record.key))}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger size="small">Delete</Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Modal
      title={<Typography.Title level={4} style={{ margin: 0 }}>AI Review Options</Typography.Title>}
      visible={isVisible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleStartReview}
          loading={loading}
          disabled={loading || (correctionType === "choices" && choices.filter(c => c.trim()).length < 2)}
          title={correctionType === "choices" && choices.filter(c => c.trim()).length < 2 ? 
            "At least two choices are required for multiple choice review" : undefined}
        >
          Start Review
        </Button>,
      ]}
      closable={!loading}
      maskClosable={!loading}
    >
      <Space direction="vertical" style={{ width: "100%", marginBottom: "24px" }} size="large">
        <Typography.Paragraph>
          An AI Review uses machine learning to analyze your dataset responses and suggest corrections. This helps ensure consistency and accuracy across your training data. You can choose between freeform corrections or predefined options below.
        </Typography.Paragraph>

        <div>
          <Divider orientation="left">
            <Space>
              Correction Type
              <Tooltip title="Choose how you want the AI to make corrections to your dataset">
                <Typography.Text type="secondary">
                  <InfoCircleOutlined style={{ fontSize: "14px" }} />
                </Typography.Text>
              </Tooltip>
            </Space>
          </Divider>
          <Radio.Group 
            onChange={handleCorrectionTypeChange} 
            value={correctionType} 
            style={{ width: "100%", display: "flex", gap: "24px" }}
          >
            <Radio value="freeform">
              <Space direction="vertical" size={0}>
                <Typography.Text strong>Freeform</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  Allow any text as a correction
                </Typography.Text>
              </Space>
            </Radio>
            <Radio value="choices">
              <Space direction="vertical" size={0}>
                <Typography.Text strong>Multiple Choice</Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
                  Define specific correction options
                </Typography.Text>
              </Space>
            </Radio>
          </Radio.Group>
        </div>

        {correctionType === "choices" && (
          <div>
            <Divider orientation="left">
              <Space>
                Correction Options
                <Tooltip title="Define the specific options that will be available for corrections">
                  <Typography.Text type="secondary">
                    <InfoCircleOutlined style={{ fontSize: "14px" }} />
                  </Typography.Text>
                </Tooltip>
              </Space>
            </Divider>
            <Form form={form} component={false}>
              <Table
                dataSource={choices.map((choice, index) => ({ key: index.toString(), choice }))}
                columns={columns}
                rowClassName="editable-row"
                pagination={false}
                bordered
                size="small"
              />
            </Form>
            <Button 
              onClick={addChoice} 
              type="dashed" 
              style={{ marginTop: "16px", width: "100%" }}
              icon={<span>+</span>}
            >
              Add Choice
            </Button>
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default AIReview; 